import React, { useState } from "react";
import { api, formatDate, formatVal, isRentflowPushed } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import { toast } from "sonner";
import { Truck, Container, RefreshCw, Building2, FlaskConical, UserRound, AlertTriangle, Send, CheckCircle2 } from "lucide-react";
import { AlvysNotesButton } from "./AlvysNotes";
import { RentflowRepushDialog } from "./RentflowRepushDialog";
import { useAuth } from "../context/AuthContext";

const statusClass = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("inactive")) return "bg-slate-100 text-slate-600 border-slate-200";
  if (s.includes("active") || s.includes("on rent")) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (s.includes("available")) return "bg-sky-50 text-sky-700 border-sky-200";
  if (s.includes("service")) return "bg-indigo-50 text-indigo-700 border-indigo-200";
  if (s.includes("maintenance")) return "bg-amber-50 text-amber-800 border-amber-200";
  if (s.includes("out")) return "bg-red-50 text-red-700 border-red-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
};

const UnitPanel = ({ bill, fleet, UnitIcon }) => (
  <div className="rounded-lg border border-border p-3">
    <div className="flex items-center justify-between gap-2 mb-2">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
        <UnitIcon className="h-3.5 w-3.5" /> Unit (via Alvys)
      </div>
      {fleet.unit_number && (
        <AlvysNotesButton notes={fleet.notes} unitNumber={fleet.unit_number} testid="fleet-rental-alvys-notes" />
      )}
    </div>
    {fleet.unit_number ? (
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div>
          <div className="text-[11px] text-muted-foreground">Unit #</div>
          <div className="text-sm font-mono font-semibold" data-testid="fleet-unit-number">{fleet.unit_number}</div>
        </div>
        <div>
          <div className="text-[11px] text-muted-foreground">Status</div>
          <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${statusClass(fleet.status)}`} data-testid="fleet-unit-status">{formatVal(fleet.status)}</span>
        </div>
        <div>
          <div className="text-[11px] text-muted-foreground">Type</div>
          <div className="text-sm">{formatVal(fleet.unit_type)}{fleet.year ? ` · ${fleet.year}` : ""}</div>
        </div>
        <div>
          <div className="text-[11px] text-muted-foreground">Make / Model</div>
          <div className="text-sm">{formatVal(fleet.make_model)}</div>
        </div>
        <div className="min-w-0">
          <div className="text-[11px] text-muted-foreground">VIN</div>
          <div className="text-sm font-mono truncate">{formatVal(fleet.vin)}</div>
        </div>
      </div>
    ) : (
      <div className="text-sm text-muted-foreground">No unit found for plate {formatVal(bill.license_plate)}.</div>
    )}
  </div>
);

const RentalPanel = ({ rentflowEnabled, rentals, error }) => {
  if (!rentflowEnabled) {
    return (
      <div className="rounded-lg border border-dashed border-border px-3 py-3 text-xs text-muted-foreground flex items-center gap-1.5" data-testid="rentflow-off-note">
        <Building2 className="h-3.5 w-3.5" /> Renter lookups (RentFlow) are turned off. Add RentFlow API credentials to enable renter data.
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground px-3 py-2 flex items-center gap-1 border-b border-border bg-secondary/40">
        <Building2 className="h-3.5 w-3.5" /> Renting customer by transaction date (via RentFlow)
      </div>
      {error ? (
        <div className="px-3 py-3 flex items-start gap-2 text-sm text-amber-800 bg-amber-50" data-testid="rentflow-error-note">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">RentFlow lookup failed</div>
            <div className="text-xs text-amber-700">{error} Fix the RentFlow API key/URL, then click “Re-sync”.</div>
          </div>
        </div>
      ) : rentals.length === 0 ? (
        <div className="px-3 py-4 text-sm text-muted-foreground">No rental records found for the transaction dates.</div>
      ) : (
        <Table data-testid="rental-table">
          <TableHeader>
            <TableRow>
              <TableHead>Transaction date</TableHead>
              <TableHead>Renting customer</TableHead>
              <TableHead>Rental period</TableHead>
              <TableHead>Contact</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rentals.map((r, i) => (
              <TableRow key={`${r.customer_id || "r"}-${r.date || ""}-${i}`} data-testid="rental-row">
                <TableCell className="text-sm">{formatDate(r.date)}</TableCell>
                <TableCell>
                  <div className="text-sm font-medium flex items-center gap-1.5"><UserRound className="h-3.5 w-3.5 text-muted-foreground" />{formatVal(r.customer_name)}</div>
                  <div className="text-xs text-muted-foreground font-mono">{formatVal(r.customer_id)}</div>
                </TableCell>
                <TableCell className="text-sm">{r.rental_start ? `${formatDate(r.rental_start)} – ${formatDate(r.rental_end)}` : "—"}</TableCell>
                <TableCell>
                  <div className="text-xs">{formatVal(r.contact_email)}</div>
                  <div className="text-xs text-muted-foreground">{formatVal(r.contact_phone)}</div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

const RentflowPushBadge = ({ push }) => {
  if (!push) return null;
  if (isRentflowPushed(push)) {
    const r = push.response || {};
    const total = (r.accepted || 0) + (r.updated || 0);
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700"
        title={`Pushed ${push.count || 0} transaction(s) to RentFlow${push.auto ? " automatically" : ""}`}
        data-testid="rentflow-push-status"
      >
        <CheckCircle2 className="h-3 w-3" /> Pushed to RentFlow{total ? ` · ${total}` : ""}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800"
      title={push.error || "RentFlow push failed"}
      data-testid="rentflow-push-status"
    >
      <AlertTriangle className="h-3 w-3" /> Push failed
    </span>
  );
};

export function FleetRental({ bill, onUpdated, rentflowEnabled = true, rentflowPushEnabled = true }) {
  const [loading, setLoading] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [confirmRepush, setConfirmRepush] = useState(false);
  const { isWriter } = useAuth();
  const fleet = bill.fleet || {};
  const rentals = bill.rental_lookups || [];
  const isMock = bill.enrichment_mode === "mock";
  const enriched = bill.enrichment_status === "completed";
  const UnitIcon = (fleet.unit_type || "").toLowerCase().includes("truck") ? Truck : Container;
  const hasData = enriched || rentals.length > 0 || fleet.unit_number || bill.rental_lookup_error;
  const push = bill.rentflow_push;
  const alreadyPushed = isRentflowPushed(push);
  const pushFailed = !!push && String(push.status || "").toLowerCase() === "error";
  // Only renter-matched bills can be pushed. Already-pushed bills can be re-pushed
  // after an explicit confirmation (RentFlow updates the record idempotently).
  const canPush = isWriter && rentflowPushEnabled && rentals.length > 0;

  const runLookup = async () => {
    setLoading(true);
    try {
      const res = await api.enrichBill(bill.id);
      toast.success("Fleet & rental data refreshed");
      onUpdated && onUpdated(res.data);
    } catch {
      toast.error("Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  const pushToRentflow = async (force = false) => {
    if (pushing) return;
    setPushing(true);
    try {
      const res = await api.pushRentflow(bill.id, { force });
      const r = res.data?.result || {};
      const total = (r.accepted || 0) + (r.updated || 0);
      toast.success(force ? "Re-pushed to RentFlow" : "Pushed to RentFlow", {
        description: `${total || res.data?.count || 0} transaction(s) sent for billing.`,
      });
      if (res.data?.bill) onUpdated && onUpdated(res.data.bill);
    } catch (e) {
      if (e?.response?.status === 503) {
        toast.error("RentFlow push is not configured.");
      } else {
        toast.error("RentFlow push failed", { description: e?.message });
      }
    } finally {
      setPushing(false);
    }
  };

  const onPushClick = () => {
    if (alreadyPushed) setConfirmRepush(true);
    else pushToRentflow(false);
  };

  return (
    <Card data-testid="fleet-rental-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Truck className="h-4 w-4 text-muted-foreground" /> Fleet &amp; Rental
          </CardTitle>
          <div className="flex items-center gap-2">
            {isMock && (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800" title="Demo data — connect Alvys & RentFlow APIs to use live data">
                <FlaskConical className="h-3 w-3" /> Demo data
              </span>
            )}
            <RentflowPushBadge push={push} />
            {canPush && (
              <Button
                size="sm"
                variant="outline"
                className="gap-1 h-7"
                onClick={onPushClick}
                disabled={pushing}
                data-testid="rentflow-push-button"
              >
                <Send className={`h-3.5 w-3.5 ${pushing ? "animate-pulse" : ""}`} />
                {alreadyPushed ? "Re-push to RentFlow" : pushFailed ? "Retry RentFlow push" : "Push to RentFlow"}
              </Button>
            )}
            {isWriter && (
              <Button size="sm" variant="outline" className="gap-1 h-7" onClick={runLookup} disabled={loading} data-testid="fleet-lookup-button">
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> {enriched ? "Re-sync" : "Look up"}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            No fleet/rental data yet. Click <span className="font-medium">Look up</span> to query {rentflowEnabled ? "Alvys & RentFlow" : "Alvys"} by plate{" "}
            <span className="font-mono">{formatVal(bill.license_plate)}</span>.
          </div>
        ) : (
          <div className="space-y-4">
            <UnitPanel bill={bill} fleet={fleet} UnitIcon={UnitIcon} />
            <RentalPanel rentflowEnabled={rentflowEnabled} rentals={rentals} error={bill.rental_lookup_error} />
          </div>
        )}
      </CardContent>
      <RentflowRepushDialog
        open={confirmRepush}
        onOpenChange={setConfirmRepush}
        push={push}
        onConfirm={() => { setConfirmRepush(false); pushToRentflow(true); }}
      />
    </Card>
  );
}
